/**
 * lib/documents.js — Iter 52 client helpers for ObjectDocuments.
 */
import { api } from "@/lib/api";

export const DOCUMENT_TYPES = [
  "SHOP_DRAWING", "SUBMITTAL", "AS_BUILT", "INSPECTION",
  "MATERIAL_CERT", "TEST_REPORT", "DELIVERY_TICKET",
  "FIELD_SKETCH", "PERMIT", "SPECIFICATION", "REFERENCE", "OTHER",
];

export const DOCUMENT_TYPE_LABEL = {
  SHOP_DRAWING: "Shop Drawing",
  SUBMITTAL: "Submittal",
  AS_BUILT: "As-Built",
  INSPECTION: "Inspection",
  MATERIAL_CERT: "Material Cert",
  TEST_REPORT: "Test Report",
  DELIVERY_TICKET: "Delivery Ticket",
  FIELD_SKETCH: "Field Sketch",
  PERMIT: "Permit",
  SPECIFICATION: "Specification",
  REFERENCE: "Reference",
  OTHER: "Other",
};

export async function listObjectDocuments(objectType, objectId) {
  try {
    const { data } = await api.get(
      `/objects/${objectType}/${objectId}/documents`);
    return data?.documents || [];
  } catch {
    return [];
  }
}

export async function listProjectDocuments(projectId) {
  try {
    const { data } = await api.get(`/projects/${projectId}/documents`);
    return data?.documents || [];
  } catch {
    return [];
  }
}

export async function uploadObjectDocument(
  objectType, objectId,
  { file, documentType = "OTHER", title, description, onProgress } = {},
) {
  const form = new FormData();
  form.append("file", file);
  form.append("document_type", documentType);
  if (title) form.append("title", title);
  if (description) form.append("description", description);
  const { data } = await api.post(
    `/objects/${objectType}/${objectId}/documents`,
    form,
    {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (e.total && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      },
    },
  );
  return data;
}

export async function bulkIntake(projectId, files, { onProgress } = {}) {
  const form = new FormData();
  for (const f of files) form.append("files", f);
  const { data } = await api.post(
    `/projects/${projectId}/document_intake`,
    form,
    {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (e.total && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    },
  );
  return data;
}

export async function listIntakeQueue(projectId, status = "pending") {
  try {
    const { data } = await api.get(
      `/projects/${projectId}/document_intake`, { params: { status } });
    return data || { intake_items: [], high_confidence_threshold: 0.7 };
  } catch {
    return { intake_items: [], high_confidence_threshold: 0.7 };
  }
}

export async function confirmIntake(queueId, override = {}) {
  const { data } = await api.post(
    `/document_intake/${queueId}/confirm`, override);
  return data;
}

export async function discardIntake(queueId) {
  await api.post(`/document_intake/${queueId}/discard`);
}

export async function deleteDocument(docId) {
  await api.delete(`/documents/${docId}`);
}

/** Returns the absolute URL the browser can render directly. The
 * backend serves these with Authorization required — since we use
 * Bearer tokens stored in localStorage, the client passes the token
 * through a fetch + blob URL when embedding. */
export function documentContentUrl(docId) {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  return `${base}/api/documents/${docId}/content`;
}

export function documentThumbnailUrl(docId) {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  return `${base}/api/documents/${docId}/thumbnail`;
}

export async function fetchBlobUrl(url) {
  const tok = localStorage.getItem("fop_bearer");
  const r = await fetch(url, {
    headers: tok ? { Authorization: `Bearer ${tok}` } : {},
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const b = await r.blob();
  return URL.createObjectURL(b);
}
